import isEqual from "lodash.isequal";

import { v4 } from "uuid";

import {
  SharedGenericComponent,
  SharedGenericComponentSchema,
} from "@intelligems/ig-types/schemas/visual-builder/mod-schemas";
import { sharedFromAiFormat } from "@src/store/slices/experienceBuilder/common/utils/sharedFromAiFormat";
import { pickSharedFields } from "@src/store/slices/experienceBuilder/common/utils/pickSharedFields";
import {
  GenericComponent,
  ModHandler,
} from "@src/store/slices/experienceBuilder/types";
import { isValidSelector } from "@src/store/slices/experienceBuilder/common/utils/html/isValidSelector";

export const genericComponentHandler: ModHandler<GenericComponent> = {
  normalize: (mod) => mod,

  convertFrom: (mod) => ({
    type: "onsiteEdit",
    dto: {
      id: mod.id,
      variationId: mod.variationId,
      querySelectors: [mod.selector],
      onsiteEditType: "html",
      replace: mod.definition,
      find: "",
      isRegex: false,
      skip: false,
      remove: false,
      isHtml: false,
      isImage: false,
      // `replace` triggers outerHTML swap via a null insertType; the
      // other 4 values pass straight through as insertAdjacentHTML
      // positions.
      insertType: mod.insertType === "replace" ? null : mod.insertType,
      createdAtUrl: mod.createdAtUrl,
      source: mod.source ?? null,
      title: mod.description ?? null,
    },
  }),

  convertTo: (conversion) => {
    if (conversion.type !== "onsiteEdit") {
      throw new Error("Expected onsiteEdit");
    }
    const edit = conversion.dto;
    return {
      type: "genericComponent",
      category: "component",
      id: edit.id,
      variationId: edit.variationId,
      selector: edit.querySelectors.join(","),
      insertType: edit.insertType ?? "replace",
      definition: edit.replace,
      createdAtUrl: edit.createdAtUrl ?? undefined,
      source: edit.source ?? undefined,
      description: edit.title ?? undefined,
    };
  },

  getErrors: (mod) => {
    const errors: Record<string, string> = {};
    if (!mod.definition) {
      errors.definition = "Definition is required";
    }
    if (!mod.selector) {
      errors.selector = "Element selection is required";
    }
    if (mod.selector && !isValidSelector(mod.selector)) {
      errors.selector = "Selector is invalid";
    }
    return errors;
  },

  isEquivalent: (a, b) =>
    b.type === "genericComponent" &&
    a.selector === b.selector &&
    a.insertType === b.insertType,

  defaults: () => ({
    id: v4(),
    type: "genericComponent",
    category: "component",
    variationId: "",
    selector: "",
    insertType: "replace",
    definition: "",
  }),

  toAiFormat: (mod) =>
    pickSharedFields<SharedGenericComponent>(SharedGenericComponentSchema, mod),

  fromAiFormat: (aiMod, context) => ({
    ...(context.existing ?? genericComponentHandler.defaults()),
    ...aiMod,
    ...sharedFromAiFormat(aiMod, context),
  }),

  hasChanges: (oldMod, newMod) => {
    const pick = (mod: GenericComponent) => ({
      definition: mod.definition,
      selector: mod.selector,
      description: mod.description,
    });
    return !isEqual(pick(oldMod), pick(newMod));
  },
};
